import { useState, useEffect } from "react";
import { useGetBackendUrl } from "./use-get-backend-url";
import { useGraphQLFetch } from "./use-graphql-fetch";
import { useAuth } from "@/providers/AuthProvider";
import { CustomFieldDefinition, CustomFieldValue, CustomFieldFormData } from "@/types/custom-fields";
import { toast } from "sonner";

const CUSTOM_FIELD_DEFINITIONS_QUERY = `
  query GetCustomFieldDefinitions($active: Boolean) {
    customFields {
      customFieldDefinitionList(active: $active) {
        customFieldDefinitionID
        fieldName
        fieldLabel
        fieldType
        isRequired
        dropdownOptions
        validationRules
        displayOrder
        isActive
        createdAt
        modifiedAt
      }
    }
  }
`;

const CREATE_CUSTOM_FIELD_DEFINITION_MUTATION = `
  mutation CreateCustomFieldDefinition($definition: CustomFieldDefinitionInput!) {
    customFields {
      create(definition: $definition) {
        __typename
        ... on IntEntityId {
          id
        }
      }
    }
  }
`;

const UPDATE_CUSTOM_FIELD_DEFINITION_MUTATION = `
  mutation UpdateCustomFieldDefinition($definition: CustomFieldDefinitionInput!) {
    customFields {
      update(definition: $definition) {
        __typename
        ... on IntEntityId {
          id
        }
      }
    }
  }
`;

const DELETE_CUSTOM_FIELD_DEFINITION_MUTATION = `
  mutation DeleteCustomFieldDefinition($id: Int!) {
    customFields {
      delete(id: $id) {
        __typename
        ... on Succeeded {
          succeeded
        }
      }
    }
  }
`;

const REORDER_CUSTOM_FIELD_DEFINITIONS_MUTATION = `
  mutation ReorderCustomFieldDefinitions($fieldIds: [Int!]!) {
    customFields {
      reorder(fieldIds: $fieldIds) {
        __typename
        ... on Succeeded {
          succeeded
        }
      }
    }
  }
`;

interface CustomFieldDefinitionNode {
  customFieldDefinitionID: number;
  fieldName: string;
  fieldLabel: string;
  fieldType: string;
  isRequired: boolean;
  dropdownOptions: string | null;
  validationRules: string | null;
  displayOrder: number;
  isActive: boolean;
  createdAt: string;
  modifiedAt: string;
}

interface CustomFieldDefinitionsResponse {
  customFields?: {
    customFieldDefinitionList?: CustomFieldDefinitionNode[];
  };
}

interface UseCustomFieldsOptions {
  /** Whether to fetch definitions on mount. Defaults to true. Set to false in demo mode. */
  enabled?: boolean;
}

export const useCustomFields = (options: UseCustomFieldsOptions = {}) => {
  const { enabled = true } = options;
  const backendUrl = useGetBackendUrl();
  const { fetchGraphQL } = useGraphQLFetch();
  const [definitions, setDefinitions] = useState<CustomFieldDefinition[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchDefinitions = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await fetchGraphQL<CustomFieldDefinitionsResponse>(CUSTOM_FIELD_DEFINITIONS_QUERY, { active: true });

      if (result.errors && result.errors.length > 0) {
        throw new Error(result.errors[0].message);
      }

      const definitionsList = result.data?.customFields?.customFieldDefinitionList || [];
      const mappedDefinitions: CustomFieldDefinition[] = definitionsList.map((node) => ({
        customFieldDefinitionID: node.customFieldDefinitionID,
        fieldName: node.fieldName,
        fieldLabel: node.fieldLabel,
        fieldType: node.fieldType as CustomFieldDefinition["fieldType"],
        isRequired: node.isRequired,
        dropdownOptions: node.dropdownOptions ?? undefined,
        validationRules: node.validationRules ?? undefined,
        displayOrder: node.displayOrder,
        isActive: node.isActive,
        createdAt: node.createdAt,
        modifiedAt: node.modifiedAt,
      }));

      setDefinitions(mappedDefinitions);
    } catch (err) {
      const message = err instanceof Error ? err.message : "An error occurred";
      setError(message);
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const createDefinition = async (data: CustomFieldFormData): Promise<CustomFieldDefinition | null> => {
    try {
      const definition = {
        customFieldDefinitionID: 0,
        fieldName: data.fieldName,
        fieldLabel: data.fieldLabel,
        fieldType: data.fieldType,
        isRequired: data.isRequired,
        dropdownOptions: data.dropdownOptions ? JSON.stringify(data.dropdownOptions) : null,
        validationRules: data.validationRules ? JSON.stringify(data.validationRules) : null,
        displayOrder: definitions.length,
      };

      const result = await fetchGraphQL<{
        customFields?: {
          create?: { __typename?: string; id?: number };
        };
      }>(CREATE_CUSTOM_FIELD_DEFINITION_MUTATION, { definition });

      if (result.errors && result.errors.length > 0) {
        throw new Error(result.errors[0].message);
      }

      const createdId = result.data?.customFields?.create?.id;
      if (!createdId) {
        throw new Error("Failed to create custom field");
      }

      const newDefinition: CustomFieldDefinition = {
        customFieldDefinitionID: createdId,
        fieldName: data.fieldName,
        fieldLabel: data.fieldLabel,
        fieldType: data.fieldType,
        isRequired: data.isRequired,
        dropdownOptions: data.dropdownOptions ? JSON.stringify(data.dropdownOptions) : undefined,
        validationRules: data.validationRules ? JSON.stringify(data.validationRules) : undefined,
        displayOrder: definitions.length,
        isActive: true,
        createdAt: new Date().toISOString(),
        modifiedAt: new Date().toISOString(),
      };

      setDefinitions([...definitions, newDefinition]);
      toast.success("Custom field created successfully");
      return newDefinition;
    } catch (err) {
      const message = err instanceof Error ? err.message : "An error occurred";
      toast.error(message);
      return null;
    }
  };

  const updateDefinition = async (id: number, data: CustomFieldFormData): Promise<boolean> => {
    try {
      const existing = definitions.find((d) => d.customFieldDefinitionID === id);
      if (!existing) throw new Error("Definition not found");

      const definition = {
        customFieldDefinitionID: id,
        fieldName: data.fieldName,
        fieldLabel: data.fieldLabel,
        fieldType: data.fieldType,
        isRequired: data.isRequired,
        dropdownOptions: data.dropdownOptions ? JSON.stringify(data.dropdownOptions) : null,
        validationRules: data.validationRules ? JSON.stringify(data.validationRules) : null,
        displayOrder: existing.displayOrder,
      };

      const result = await fetchGraphQL<{
        customFields?: {
          update?: { __typename?: string; id?: number };
        };
      }>(UPDATE_CUSTOM_FIELD_DEFINITION_MUTATION, { definition });

      if (result.errors && result.errors.length > 0) {
        throw new Error(result.errors[0].message);
      }

      const updatedId = result.data?.customFields?.update?.id;
      if (!updatedId) {
        throw new Error("Failed to update custom field");
      }

      const updated: CustomFieldDefinition = {
        ...existing,
        fieldName: data.fieldName,
        fieldLabel: data.fieldLabel,
        fieldType: data.fieldType,
        isRequired: data.isRequired,
        dropdownOptions: data.dropdownOptions ? JSON.stringify(data.dropdownOptions) : undefined,
        validationRules: data.validationRules ? JSON.stringify(data.validationRules) : undefined,
        modifiedAt: new Date().toISOString(),
      };

      setDefinitions(definitions.map((d) => (d.customFieldDefinitionID === id ? updated : d)));
      toast.success("Custom field updated successfully");
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : "An error occurred";
      toast.error(message);
      return false;
    }
  };

  const deleteDefinition = async (id: number): Promise<boolean> => {
    try {
      const result = await fetchGraphQL<{
        customFields?: {
          delete?: { __typename?: string; succeeded?: boolean };
        };
      }>(DELETE_CUSTOM_FIELD_DEFINITION_MUTATION, { id });

      if (result.errors && result.errors.length > 0) {
        throw new Error(result.errors[0].message);
      }

      const succeeded = result.data?.customFields?.delete?.succeeded;
      if (!succeeded) {
        throw new Error("Failed to delete custom field");
      }

      setDefinitions(definitions.filter((d) => d.customFieldDefinitionID !== id));
      toast.success("Custom field deleted successfully");
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : "An error occurred";
      toast.error(message);
      return false;
    }
  };

  const reorderDefinitions = async (fieldIds: number[]): Promise<boolean> => {
    try {
      const result = await fetchGraphQL<{
        customFields?: {
          reorder?: { __typename?: string; succeeded?: boolean };
        };
      }>(REORDER_CUSTOM_FIELD_DEFINITIONS_MUTATION, { fieldIds });

      if (result.errors && result.errors.length > 0) {
        throw new Error(result.errors[0].message);
      }

      const succeeded = result.data?.customFields?.reorder?.succeeded;
      if (!succeeded) {
        throw new Error("Failed to reorder custom fields");
      }

      // Update local state
      const reordered = fieldIds
        .map((id, index) => {
          const def = definitions.find((d) => d.customFieldDefinitionID === id);
          return def ? { ...def, displayOrder: index } : null;
        })
        .filter((d): d is CustomFieldDefinition => d !== null);

      setDefinitions(reordered);
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : "An error occurred";
      toast.error(message);
      return false;
    }
  };

  useEffect(() => {
    if (enabled) {
      fetchDefinitions();
    }
  }, [enabled]);

  return {
    definitions,
    isLoading,
    error,
    fetchDefinitions,
    createDefinition,
    updateDefinition,
    deleteDefinition,
    reorderDefinitions,
  };
};

interface UseCustomFieldValuesOptions {
  /** Whether to fetch values on mount. Defaults to true. Set to false in demo mode. */
  enabled?: boolean;
}

export const useCustomFieldValues = (stockItemId: number, options: UseCustomFieldValuesOptions = {}) => {
  const { enabled = true } = options;
  const backendUrl = useGetBackendUrl();
  const { instanceId } = useAuth();
  const [values, setValues] = useState<CustomFieldValue[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchValues = async () => {
    if (!stockItemId || !enabled) return;
    setIsLoading(true);
    try {
      const headers: Record<string, string> = {};
      if (instanceId) {
        headers["X-Instance-Id"] = instanceId;
      }
      const response = await fetch(`${backendUrl}/api/CustomFieldValues/stock-item/${stockItemId}`, { headers });
      if (!response.ok) throw new Error("Failed to fetch custom field values");
      const data = await response.json();
      setValues(data);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const bulkUpsertValues = async (fieldValues: { customFieldDefinitionID: number; fieldValue: string }[]): Promise<boolean> => {
    try {
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      if (instanceId) {
        headers["X-Instance-Id"] = instanceId;
      }
      const response = await fetch(`${backendUrl}/api/CustomFieldValues/bulk-upsert`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          stockItemId: stockItemId,
          values: fieldValues.map((fv) => ({
            customFieldDefinitionID: fv.customFieldDefinitionID,
            fieldValue: fv.fieldValue,
            stockItemID: stockItemId,
            customFieldValueID: 0,
          })),
        }),
      });

      if (!response.ok) throw new Error("Failed to save custom field values");

      await fetchValues();
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : "An error occurred";
      toast.error(message);
      return false;
    }
  };

  useEffect(() => {
    if (enabled) {
      fetchValues();
    }
  }, [stockItemId, enabled]);

  return {
    values,
    isLoading,
    fetchValues,
    bulkUpsertValues,
  };
};
